import { useCallback, useEffect, useMemo, useRef } from 'react';
import { Chart } from 'chart.js';
import { createVerticalBarChart } from './util/VerticalBarChart';
import { useBarChartScrollContainer } from '@/features/charts/useBarChartScrollContainer';
import BarChartIcon from '../../../assets/images/sidebar/analysis_icon.png';
import { TransitionEventItems } from '@/types/AnalysisResultType';
import { removeYearFromDate, removeZeroPaddingFromDate, getWeekday } from '@/utils/time';
import { useTranslation } from 'react-i18next';
import { getHoursAndMinutes } from '@/utils/time';

type NormalVerticalBarChartProps = {
    data: TransitionEventItems[];
    previousData?: TransitionEventItems[]; // 前期データ（オプション）
};

/**
 * バーグラフコンポーネント
 * @param data - 今期データ
 * @param previousData - 前期データ（オプション）
 * @returns バーグラフ
 */
const NormalVerticalBarChart = ({ data, previousData }: NormalVerticalBarChartProps) => {
    const canvasRef = useRef<HTMLCanvasElement | null>(null);
    const chartRef = useRef<Chart<'bar' | 'line'> | null>(null);
    const { t, i18n } = useTranslation('analysis');
    const labelCount = data.length;
    const { scrollRef, innerWidthPx, containerWidth, minRequiredWidthPx } =
        useBarChartScrollContainer(labelCount);

    const monthAwareLabel = useCallback(
        (currentDate: string, previousDate?: string) => {
            if (!currentDate) return '';
            const curNorm = currentDate.includes('/') ? currentDate.replace(/\//g, '-') : currentDate;
            const prevNorm = previousDate
                ? previousDate.includes('/')
                    ? previousDate.replace(/\//g, '-')
                    : previousDate
                : undefined;
            const curMonth = curNorm.split('-')[1];
            const prevMonth = prevNorm?.split('-')[1];
            const trimmed = removeZeroPaddingFromDate(removeYearFromDate(curNorm));
            const [m, d] = trimmed.split('-');
            const base = !prevMonth || prevMonth !== curMonth ? `${m}/${d}` : d;
            const weekday = getWeekday(curNorm, i18n.language);
            if (!weekday) return base.replace(/-/g, '/');
            const isJa = i18n.language?.startsWith('ja');
            const shortW = isJa ? weekday.slice(0, 1) : weekday.slice(0, 2);
            const display = base.replace(/-/g, '/');
            return `${display}(${shortW})`;
        },
        [i18n.language],
    );

    const formatDuration = useCallback((minutes: number) => {
        const [hours, mins] = getHoursAndMinutes(minutes);
        if (hours === 0) {
            return `${mins}m`;
        }
        if (mins === 0) {
            return `${hours}h`;
        }
        return `${hours}h${mins}m`;
    }, []);

    const averageAllLabel = useMemo(() => t('chart.tooltip.averageAll'), [t]);
    const averageActiveLabel = useMemo(() => t('chart.tooltip.averageActive'), [t]);
    const previousLabel = useMemo(() => t('chart.tooltip.previous'), [t]);

    useEffect(() => {
        // データが空の場合はチャートを作成しない
        if (data.length === 0) return;

        // キャンバスが存在しない場合はチャートを作成しない
        if (!canvasRef.current) return;

        // 既存のチャートがあれば先に破棄
        if (chartRef.current) {
            chartRef.current.destroy();
            chartRef.current = null;
        }

        // コンテキストを取得
        const ctx = canvasRef.current.getContext('2d');
        if (!ctx) return;

        // 今期用ラベル（月替わり時だけ月/日、それ以外は日）
        const labels = data.map((item, idx) =>
            monthAwareLabel(item.date, idx > 0 ? data[idx - 1].date : undefined),
        );

        // 前期用ラベル（前期データがある場合のみ同様の処理）
        const prevLabels = previousData?.map((item, idx, arr) =>
            monthAwareLabel(item.date, idx > 0 ? arr[idx - 1].date : undefined),
        );

        const currentVals = data.map((d) => d.time);

        // 前期データがある場合は、用意しておく
        let prevVals: number[] | undefined = undefined;
        if (previousData && previousData.length) {
            // index 対応で前期値を並べる（不足分は 0）
            prevVals = data.map((_, idx) =>
                idx < previousData.length ? previousData[idx].time : 0,
            );
        }

        // チャート作成
        chartRef.current =
            createVerticalBarChart(ctx, labels, currentVals, prevLabels, prevVals, {
                averageAllLabel,
                averageActiveLabel,
                previousLabel,
                formatDuration,
                formatShortDuration: formatDuration,
            }) ?? null;

        // アンマウント時
        return () => {
            // チャートを破棄
            chartRef.current?.destroy();
        };
    }, [
        data,
        previousData,
        averageAllLabel,
        averageActiveLabel,
        previousLabel,
        formatDuration,
    ]);

    useEffect(() => {
        chartRef.current?.resize();
    }, [innerWidthPx]);

    // レンダリング
    if (data.length === 0) {
        // データが空の場合
        return (
            <div className="flex flex-col items-center justify-center h-full">
                <img src={BarChartIcon} alt={t('chart.noData')} className="w-20 h-20" />
                <p className="text-xl mt-4">{t('chart.noData')}</p>
            </div>
        );
    }

    // データがある場合：積み上げ縦棒グラフと同じレイアウトで横スクロール対応
    return (
        <div className="w-full h-full flex flex-col">
            <div
                ref={scrollRef}
                className="relative flex-1 min-h-0 overflow-x-auto overflow-y-hidden"
            >
                <div
                    className="inline-block h-full align-top"
                    style={{ width: innerWidthPx, minWidth: minRequiredWidthPx || undefined }}
                >
                    <canvas ref={canvasRef} className="block h-full w-full max-w-none" />
                </div>
            </div>
            {minRequiredWidthPx > containerWidth && containerWidth > 0 ? (
                <p className="mt-1 px-2 text-center text-xs text-gray-400">
                    {t('chart.scrollRight', { defaultValue: '右にスクロールできます →' })}
                </p>
            ) : null}
        </div>
    );
};

export default NormalVerticalBarChart;
